import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiPhone, FiMapPin, FiAlertTriangle, FiTool } from 'react-icons/fi';

const SuspensionRepair = () => {
  const signs = [
    { symptom: 'Pulling to one side', cause: 'Alignment issues, worn components' },
    { symptom: 'Bouncing after bumps', cause: 'Shot shocks or struts' },
    { symptom: 'Nose diving when braking', cause: 'Front strut failure' },
    { symptom: 'Clunking over bumps', cause: 'Worn bushings, ball joints' },
    { symptom: 'Uneven tire wear', cause: 'Bad alignment, worn suspension' },
    { symptom: 'Steering wheel vibration', cause: 'Tie rod ends, wheel balance' }
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen">
      <Helmet>
        <title>Suspension Repair Dallas | Shocks, Struts, Alignment | Paradise-Performance</title>
        <meta name="description" content="Dallas suspension repair experts. Shocks, struts, ball joints, alignments. Stop the bouncing and pulling. Same-day service. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/suspension-repair" />
      </Helmet>

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-purple-700 to-purple-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/assets/assets/images/new/suspension/dallas-suspension-repair-client-Paradise-performance1.webp"
            alt="Suspension repair service"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-5xl font-bold mb-6">Suspension Repair Dallas</h1>
          <p className="text-xl mb-8 max-w-3xl">
            Tired of feeling every pothole? That pulling to the right isn't "just how it drives." 
            Dallas roads are rough enough—your suspension shouldn't make it worse.
          </p>
          <a href="tel:469-928-8011" className="inline-block bg-white text-purple-700 px-8 py-4 rounded-lg font-bold hover:bg-gray-100">
            Fix Your Ride: (817) 382-2316
          </a>
        </div>
      </section>

      {/* Reality Check */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-8">Your Suspension Is Probably Shot If...</h2>
          <p className="text-lg text-gray-700 mb-8">
            Dallas potholes don't play nice. Add in our heat cycling and sudden storms, 
            and your suspension takes a beating. Most cars need struts by 60-80k miles, 
            but nobody tells you that until something breaks.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {signs.map((item, idx) => (
              <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                <p className="font-bold text-purple-700 mb-1">
                  <FiAlertTriangle className="inline mr-2" />{item.symptom}
                </p>
                <p className="text-gray-600 text-sm">{item.cause}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="relative h-80 overflow-hidden rounded-lg shadow-xl">
              <img 
                src="/assets/assets/images/new/suspension/dallas-suspension-repair-client-Paradise-performance2.webp"
                alt="Professional suspension service"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white font-bold">Strut & Shock Replacement</p>
                <p className="text-white/80 text-sm">Complete suspension overhaul services</p>
              </div>
            </div>
            <div className="relative h-80 overflow-hidden rounded-lg shadow-xl">
              <img 
                src="/assets/assets/images/new/suspension/dallas-suspension-repair-client-Paradise-performance3.webp"
                alt="Alignment and suspension work"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white font-bold">Precision Alignment</p>
                <p className="text-white/80 text-sm">Hunter alignment system for perfect tracking</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-8">Complete Suspension Services</h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-xl font-bold mb-2">Shock & Strut Replacement</h3>
              <p className="text-gray-700 mb-2">
                Complete assemblies or just the shock cartridges—we'll save you money where possible. 
                Most cars done same day. Lifetime warranty on parts.
              </p>
              <p className="font-bold text-green-600">Starting at $399 per axle</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-xl font-bold mb-2">4-Wheel Alignment</h3>
              <p className="text-gray-700 mb-2">
                Computerized Hunter alignment system gets you tracking straight. 
                Includes before/after printout so you see the difference.
              </p>
              <p className="font-bold text-green-600">$89.99 (Free with 4-tire purchase)</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-xl font-bold mb-2">Ball Joints & Bushings</h3>
              <p className="text-gray-700 mb-2">
                Stop that clunking and restore tight steering. We use OEM-quality parts 
                that last, not cheap aftermarket junk.
              </p>
              <p className="font-bold text-green-600">Quote varies by vehicle</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Builder */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-purple-50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold mb-4">Why We're Different</h3>
            <p className="text-gray-700 mb-4">
              Chain shops push the most expensive option. Dealers charge $200/hour labor. 
              We? We actually inspect your suspension and fix what needs fixing—nothing more, nothing less.
            </p>
            <p className="font-semibold">
              Example: Customer came in for new struts ($1,200 quote elsewhere). 
              Turned out to be a $45 sway bar link. We fixed the actual problem, saved them $1,155.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-purple-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Smooth Ride, Fair Price</h2>
          <p className="text-xl mb-8">Stop white-knuckling the steering wheel on every bump.</p>
          <a href="tel:469-928-8011" className="inline-block bg-white text-purple-700 px-10 py-5 rounded-lg font-bold text-lg hover:bg-gray-100">
            <FiPhone className="inline mr-2" /> (817) 382-2316
          </a>
          <p className="mt-6"><FiMapPin className="inline mr-2" />305 Co Rd 3470
Paradise, TX 76073</p>
        </div>
      </section>
    </motion.div>
  );
};

export default SuspensionRepair;
